import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  AfterViewInit,
  Renderer2,
  HostListener,
  Input
} from '@angular/core';

import { Visualizer } from './visualizer';
import { AudioSequence } from './audiosequence';
import { OpusWrapper } from './encoding/opus-wrapper';
import { Encoder } from './encoding/encoder';
import { Decoder } from './encoding/decoder';

declare var MediaRecorder: any;
export class TpAudioEditorComponent implements OnInit, AfterViewInit {
/**
 * @param {?} value
 * @return {?}
 */
set inputBlob(value: Blob) {
    if (value) {
      this.decodeBlob(value);
    }
  }
private canvas: ElementRef;
private progressBar: ElementRef;
private ctx: CanvasRenderingContext2D;
private mediaRecorder: any;
private chunks = [];
private actx = new AudioContext();
private visualizer: Visualizer;
private showInterval = false;
private intervalStart = 0;
private intervalEnd = 0;
private dragStart = 0;
private clipboardBuffer: Float32Array[] = [];

  track: AudioSequence;
  formats = [
    'wav',
    'mp3',
    'opus'
  ];
  selectedFormat: string;

  recordLabel = 'Record';
  fileLabel = '';
  durationLabel = '00:00 / 00:00';

  // Needed to expose the enum to the template
  PluginState = PluginState;
  state: PluginState = PluginState.IDLE;
/**
 * @param {?} renderer
 */
constructor(private renderer: Renderer2) { }
/**
 * @return {?}
 */
ngOnInit(): void {
    this.selectedFormat = this.formats[0];

    this.ctx = ( /** @type {?} */((<HTMLCanvasElement>this.canvas.nativeElement))).getContext('2d');

    this.renderer.setProperty(
      this.canvas.nativeElement,
      'width',
      this.renderer.parentNode(this.canvas.nativeElement).clientWidth
    );

    if (navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
        this.mediaRecorder = new MediaRecorder(stream);

        this.mediaRecorder.ondataavailable = (e) => {
          this.chunks.push(e.data);
        };
        this.mediaRecorder.onstop = () => {
          this.state = PluginState.IDLE;
          const /** @type {?} */ blob = new Blob(this.chunks, { 'type': 'audio/ogg;codecs=opus' });
          this.chunks = [];
          this.decodeBlob(blob);
        };
      }, err => {
        console.error(err);
      });
    }
  }
/**
 * @return {?}
 */
ngAfterViewInit(): void {
    window.requestAnimationFrame(() => this.repaint());
  }
/**
 * @return {?}
 */
public getBlob(): Promise<Blob> {
    if (this.track) {
      return Encoder.encode(this.track.getBuffer(), this.selectedFormat);
    } else {
      return Promise.reject('No audio file to get');
    }
  }
/**
 * @param {?} event
 * @return {?}
 */
handleKeyup(event: KeyboardEvent) {
    if (event.key !== 'Delete' || !this.track) {
      return;
    }
    if ((this.intervalStart === 0 && this.intervalEnd === 0) || !this.showInterval) {
      return;
    }
    this.trim(this.intervalStart, this.intervalEnd);
  }
/**
 * @param {?} e
 * @return {?}
 */
onResize(e: UIEvent): void {
    // Resize the canvas if the window gets resized
    this.renderer.setProperty(
      this.canvas.nativeElement,
      'width',
      this.renderer.parentNode(this.canvas.nativeElement).clientWidth
    );
  }
/**
 * @param {?} e
 * @return {?}
 */
onCopy(e: ClipboardEvent): void {
    if (this.showInterval) {
      this.clipboardBuffer = this.track.getSection(this.intervalStart, this.intervalEnd);
    }
  }
/**
 * @param {?} e
 * @return {?}
 */
onCut(e: ClipboardEvent): void {
    if (this.showInterval) {
      this.clipboardBuffer = this.track.getSection(this.intervalStart, this.intervalEnd);
      this.trim(this.intervalStart, this.intervalEnd);
    }
  }
/**
 * @param {?} e
 * @return {?}
 */
onPaste(e: ClipboardEvent): void {
    if (!this.showInterval && this.clipboardBuffer.length > 0) {
      this.track.insert(this.intervalStart, this.clipboardBuffer);
      this.visualizer.updateBuffer(this.track.getBuffer());
      this.stop();
    }
  }
/**
 * @param {?} e
 * @return {?}
 */
mousedown(e: MouseEvent) {
    if (!this.track) {
      return;
    }
    this.dragStart = this.getXCoordinate(e);
  }
/**
 * @param {?} e
 * @return {?}
 */
mousemove(e: MouseEvent) {
    if (!this.track) {
      return;
    }
    const /** @type {?} */ dx = Math.abs(e.clientX - this.dragStart);
    if (dx >= 5 && e.button === 0 && this.dragStart > 0) {
      const /** @type {?} */ x = this.getXCoordinate(e);
      const /** @type {?} */ width = this.canvas.nativeElement.width;
      this.intervalStart = (Math.min(this.dragStart, x) / width) * this.track.getDuration();
      this.intervalEnd = (Math.max(this.dragStart, x) / width) * this.track.getDuration();
      this.showInterval = true;
    }
  }
/**
 * @param {?} e
 * @return {?}
 */
mouseup(e: MouseEvent) {
    if (!this.track) {
      return;
    }
    const /** @type {?} */ x = this.getXCoordinate(e);
    const /** @type {?} */ dx = Math.abs(x - this.dragStart);
    if (dx < 5) {
      this.showInterval = false;
      this.intervalStart = (x / this.canvas.nativeElement.width) * this.track.getDuration();
      this.intervalEnd  = this.track.getDuration();
    }
    this.track.setCurrentTime(this.intervalStart);
    this.dragStart = 0;
  }
/**
 * @param {?} files
 * @return {?}
 */
onFileDropped(files: FileList) {
    if (this.state !== PluginState.IDLE) {
      return;
    }
    const /** @type {?} */ file = files[0];
    if (!this.isValidFile(file)) {
      return;
    }
    this.fileLabel = file.name;
    this.decodeBlob(file);
  }
/**
 * @param {?} e
 * @return {?}
 */
fileUploaded(e): void {
    if (e.target.files.length < 1) {
      return;
    }
    const /** @type {?} */ file = e.target.files[0];
    if (!this.isValidFile(file)) {
      return;
    }
    // So that if the same file is uploaded, the onchange event will get fired
    e.target.value = null;

    this.fileLabel = file.name;
    this.decodeBlob(file);
  }
/**
 * @return {?}
 */
record(): void {
    if (!this.mediaRecorder) {
      return;
    }
    if (this.mediaRecorder.state === 'inactive') {
      if (this.track) {
        this.track.stop();
      }
      this.fileLabel = '';
      this.mediaRecorder.start();
      this.state = PluginState.RECORDING;
      this.recordLabel = 'Stop Recording';
    } else if (this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
      this.state = PluginState.IDLE;
      this.recordLabel = 'Record';
    }
  }
/**
 * @return {?}
 */
play(): void {
    if (!this.track) {
      return;
    }
    this.track.play(this.intervalStart, this.intervalEnd);
  }
/**
 * @return {?}
 */
pause(): void {
    if (!this.track) {
      return;
    }
    this.track.pause();
  }
/**
 * @return {?}
 */
stop(): void {
    if (!this.track) {
      return;
    }
    this.intervalStart = 0;
    this.intervalEnd = 0;
    this.showInterval = false;
    this.track.stop();
  }
/**
 * @return {?}
 */
download() {
    if (!this.track) {
      return;
    }

    this.state = PluginState.DOWNLOADING;
    this.setIndeterminate(true);

    const /** @type {?} */ buffer = this.track.getBuffer();
    Encoder.encode(buffer, this.selectedFormat).then(blob => {
      this.state = PluginState.IDLE;
      this.setProgress(1);
      this.downloadBlob(blob);
    }, err => {
      this.state = PluginState.IDLE;
      this.setProgress(1);
      console.error(err.message || err);
    });
  }
/**
 * @param {?} e
 * @return {?}
 */
private getXCoordinate(e: MouseEvent): number {
    const /** @type {?} */ target = /** @type {?} */(( <any>e.currentTarget));
    return e.pageX - target.offsetLeft;
  }
/**
 * @param {?} isIndeterminate
 * @return {?}
 */
private setIndeterminate(isIndeterminate: boolean): void {
    if (isIndeterminate) {
      this.renderer.removeAttribute(this.progressBar.nativeElement, 'value');
    } else {
      this.setProgress(0);
    }
  }
/**
 * @param {?} progress
 * @return {?}
 */
private setProgress(progress: number): void {
    this.renderer.setAttribute(this.progressBar.nativeElement, 'value', '' + progress);
  }
/**
 * @param {?} file
 * @return {?}
 */
private isValidFile(file: File): boolean {
    return file && (file.name.endsWith('.mp3') || file.name.endsWith('.wav') || file.name.endsWith('.opus'));
  }
/**
 * @param {?} blob
 * @return {?}
 */
private decodeBlob(blob: Blob | File): void {
    this.state = PluginState.DECODING;
    this.setIndeterminate(true);
    this.clipboardBuffer = [];
    this.stop();

    Decoder.decode(blob, this.actx).then(decodedData => {
      this.stop();
      this.track = new AudioSequence(decodedData, this.actx);
      if (!this.visualizer) {
        this.visualizer = new Visualizer(this.canvas, decodedData);
      } else {
        this.visualizer.updateBuffer(decodedData);
      }
      this.state = PluginState.IDLE;
      this.setProgress(1);
    }, err => {
      this.state = PluginState.IDLE;
      this.setProgress(1);
      console.error(err.message || err);
    });
  }
/**
 * @param {?} blob
 * @return {?}
 */
private downloadBlob(blob: Blob): void {
    const /** @type {?} */ link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'out.' + this.selectedFormat;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
/**
 * @param {?} start
 * @param {?} end
 * @return {?}
 */
private trim(start: number, end: number) {
    this.track.trim(start, end);
    this.visualizer.updateBuffer(this.track.getBuffer());
    this.showInterval = false;
    this.stop();
  }
/**
 * @param {?} current
 * @param {?} total
 * @return {?}
 */
private formatDuration(current: number, total: number): void {
    const /** @type {?} */ currentStr = new Date(current * 1000).toISOString().substr(14, 9);
    const /** @type {?} */ totalStr = new Date(total * 1000).toISOString().substr(14, 9);
    this.durationLabel = currentStr + ' / ' + totalStr;
  }
/**
 * @return {?}
 */
private drawPlayhead(): void {
    if (!this.track) {
      return;
    }
    const /** @type {?} */ x = Math.floor((this.track.getCurrentTime() / this.track.getDuration()) * this.canvas.nativeElement.width);
    if (x === 0) {
      return;
    }
    this.ctx.save();
    this.ctx.strokeStyle = '#008F93';
    this.ctx.translate(0, 0);
    this.ctx.beginPath();
    this.ctx.moveTo(x, 0);
    this.ctx.lineTo(x, this.canvas.nativeElement.height);
    this.ctx.stroke();
    this.ctx.restore();
  }
/**
 * @return {?}
 */
private drawInterval(): void {
    if (!this.showInterval || !this.track) {
      return;
    }
    const /** @type {?} */ x0 = (this.intervalStart / this.track.getDuration()) * this.canvas.nativeElement.width;
    const /** @type {?} */ x1 = (this.intervalEnd / this.track.getDuration()) * this.canvas.nativeElement.width;
    this.ctx.save();
    this.ctx.fillStyle = 'rgba(80, 133, 229, 0.3)';
    this.ctx.fillRect(x0, 0, x1 - x0, this.canvas.nativeElement.height);
    this.ctx.restore();
  }
/**
 * @return {?}
 */
private repaint(): void {
    this.ctx.save();
    this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    this.ctx.restore();
    if (this.visualizer) {
      this.visualizer.draw();
    }
    this.drawInterval();
    this.drawPlayhead();
    if (this.track) {
      this.formatDuration(this.track.getCurrentTime(), this.track.getDuration());
    }
    window.requestAnimationFrame(() => this.repaint());
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'tp-audio-editor',
  template: `
    <canvas 
        DragAndDrop (fileDropEmitter)="onFileDropped($event)"
        #visualizer height="125px" width="100px"
        (mousedown)="mousedown($event)" 
        (mouseup)="mouseup($event)" 
        (mousemove)="mousemove($event)">
    </canvas>
    <div class="btn-container">
      <label id="durationLabel">{{durationLabel}}</label>
      <button 
        [disabled]="state === PluginState.DECODING || state === PluginState.DOWNLOADING" 
        (click)="record()">
        {{recordLabel}}
      </button>
      <button [disabled]="!track || state === PluginState.DECODING || state === PluginState.RECORDING" (click)="play()">Play</button>
      <button [disabled]="!track || state === PluginState.DECODING || state === PluginState.RECORDING" (click)="pause()">Pause</button>
      <button [disabled]="!track || state === PluginState.DECODING || state === PluginState.RECORDING" (click)="stop()">Stop</button>
    </div>
    <div class="file-container">
          <input 
            [disabled]="state === PluginState.DECODING || state === PluginState.DOWNLOADING" 
            type="file" id="file" name="file" accept=".mp3, .opus, .wav" (change)="fileUploaded($event)"/>
        <label
          [ngClass]="{'upload-disabled': state === PluginState.DECODING || state === PluginState.DOWNLOADING}" 
          for="file">
          Upload
        </label>
        <label id="file-label">{{fileLabel}}</label>
        <div class="download-container">
            <select 
              [(ngModel)]="selectedFormat" 
              [disabled]="state === PluginState.DOWNLOADING || state === PluginState.DECODING || !track">
                <option *ngFor="let f of formats" [value]="f">{{f}}</option>
            </select>
            <button 
              (click)="download()" 
              [disabled]="state === PluginState.DOWNLOADING || state === PluginState.DECODING || !track">
              Download
            </button>
        </div>
    </div>
    <progress #progressBar value="0" max="1"></progress>
  `,
  styles: [`
    :host {
      width: 100%;
      height: 100%;
      display: block;
      font-family: Arial, Helvetica, sans-serif;
      -webkit-box-sizing: border-box;
              box-sizing: border-box;
    }

    button, #file + label {
        font-size: 0.8em;
        padding: 10px;
        margin: 10px;
        background-color: white;
        border: 1px solid cornflowerblue;
        color: #505050;
        -webkit-transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
        transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
        cursor: pointer;
    }

    #visualizer {
        display: block;
    }

    button:hover:enabled, #file + label:hover {
        background-color: cornflowerblue;
        color: white;
    }

    button:disabled, #file + label:disabled {
        color: #C6C6C6;
        border-color: #C6C6C6;
        cursor: default;
    }

    .btn-container {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-flow: row wrap;
                flex-flow: row wrap;
        -ms-flex-line-pack: start;
            align-content: flex-start;
    }

    .btn-container > button {
        -webkit-box-flex: 1;
            -ms-flex-positive: 1;
                flex-grow: 1;
        -ms-flex-preferred-size: 0;
            flex-basis: 0;
    }

    #durationLabel {
        -webkit-box-flex: 0;
            -ms-flex: 0 1 100%;
                flex: 0 1 100%;
        margin-left: 10px;
        font-size: 0.8em;
        color: #505050;
    }

    #file {
        width: 0.1px;
        height: 0.1px;
        opacity: 0;
        overflow: hidden;
        position: absolute;
        z-index: -1;
    }

    #file:focus + label {
        outline: 1px dotted #000;
        outline: -webkit-focus-ring-color auto 5px;
    }

    #file + label {
        text-align: center;
    }
    #file-label {
        font-size: 0.8em;
        color: #505050;
        margin-left: 10px;
    }

    .file-container {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-align: center;
            -ms-flex-align: center;
                align-items: center;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
    }

    .download-container {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        margin-left: auto;
    }

    .download-container > select {
        margin: 10px;
        border-color: cornflowerblue;
    }

    .download-container > select:disabled {
        border-color: #C6C6C6;    
    }

    .upload-disabled {
        border-color: #C6C6C6 !important;
        color: #C6C6C6 !important;
    }

    .upload-disabled:hover {
        background-color: white !important;
        cursor: default !important;
    }

    progress {
        margin-left: 10px;
        width: calc(100% - 20px);
    }
  `],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'inputBlob': [{ type: Input },],
'canvas': [{ type: ViewChild, args: ['visualizer', ] },],
'progressBar': [{ type: ViewChild, args: ['progressBar', ] },],
'handleKeyup': [{ type: HostListener, args: ['document:keyup', ['$event'], ] },],
'onResize': [{ type: HostListener, args: ['window:resize', ['$event'], ] },],
'onCopy': [{ type: HostListener, args: ['document:copy', ['$event'], ] },],
'onCut': [{ type: HostListener, args: ['document:cut', ['$event'], ] },],
'onPaste': [{ type: HostListener, args: ['document:paste', ['$event'], ] },],
};
}

function TpAudioEditorComponent_tsickle_Closure_declarations() {
/** @type {?} */
TpAudioEditorComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TpAudioEditorComponent.ctorParameters;
/** @type {?} */
TpAudioEditorComponent.propDecorators;
/** @type {?} */
TpAudioEditorComponent.prototype.canvas;
/** @type {?} */
TpAudioEditorComponent.prototype.progressBar;
/** @type {?} */
TpAudioEditorComponent.prototype.ctx;
/** @type {?} */
TpAudioEditorComponent.prototype.mediaRecorder;
/** @type {?} */
TpAudioEditorComponent.prototype.chunks;
/** @type {?} */
TpAudioEditorComponent.prototype.actx;
/** @type {?} */
TpAudioEditorComponent.prototype.visualizer;
/** @type {?} */
TpAudioEditorComponent.prototype.showInterval;
/** @type {?} */
TpAudioEditorComponent.prototype.intervalStart;
/** @type {?} */
TpAudioEditorComponent.prototype.intervalEnd;
/** @type {?} */
TpAudioEditorComponent.prototype.dragStart;
/** @type {?} */
TpAudioEditorComponent.prototype.clipboardBuffer;
/** @type {?} */
TpAudioEditorComponent.prototype.track;
/** @type {?} */
TpAudioEditorComponent.prototype.formats;
/** @type {?} */
TpAudioEditorComponent.prototype.selectedFormat;
/** @type {?} */
TpAudioEditorComponent.prototype.recordLabel;
/** @type {?} */
TpAudioEditorComponent.prototype.fileLabel;
/** @type {?} */
TpAudioEditorComponent.prototype.durationLabel;
/** @type {?} */
TpAudioEditorComponent.prototype.PluginState;
/** @type {?} */
TpAudioEditorComponent.prototype.state;
/** @type {?} */
TpAudioEditorComponent.prototype.renderer;
}

export type PluginState = number;
export let PluginState: any = {};
PluginState.IDLE = 0;
PluginState.DOWNLOADING = 1;
PluginState.RECORDING = 2;
PluginState.DECODING = 3;
PluginState[PluginState.IDLE] = "IDLE";
PluginState[PluginState.DOWNLOADING] = "DOWNLOADING";
PluginState[PluginState.RECORDING] = "RECORDING";
PluginState[PluginState.DECODING] = "DECODING";


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
